import React, { useState, useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import { api, useAxiosInterceptor } from "../shared/axiosInstance";
import FieldHelpPopover from "./FieldHelpPopover";
import { postcodeValidator } from "postcode-validator";
import countries from "i18n-iso-countries";
import en from "i18n-iso-countries/langs/en.json";
import getSymbolFromCurrency from "currency-symbol-map";
import localeCurrency from "locale-currency";

const getCurrencySymbolFromCountry = (countryCode) => {
    try {
        const currencyCode = localeCurrency.getCurrency(countryCode.toUpperCase());
        return getSymbolFromCurrency(currencyCode) || "$";
    } catch {
        return "$";
    }
};

countries.registerLocale(en);

const JobPostModal = ({ isOpen, onClose, onSave, job }) => {
    useAxiosInterceptor();

    const [jobTitle, setJobTitle] = useState("");
    const [jobUrl, setJobUrl] = useState("");
    const [targets, setTargets] = useState([]);
    const [status, setStatus] = useState("Open");
    const [loading, setLoading] = useState(false);
    const [salaryMin, setSalaryMin] = useState("");
    const [salaryMax, setSalaryMax] = useState("");
    const [postalCode, setPostalCode] = useState("");
    const [country, setCountry] = useState("US");
    const [offersRelocationAssistance, setOffersRelocationAssistance] = useState(true);
    const [offersSponsorship, setOffersSponsorship] = useState(true);
    const [allTargetSuggestions, setAllTargetSuggestions] = useState([]);

    useEffect(() => {
        if (isOpen) {
            api.get("/employer/jobTargets")
                .then((res) => setAllTargetSuggestions(res.data || []))
                .catch((err) => console.error("Error fetching target suggestions", err));
        }
    }, [isOpen]);

    const formatNumber = (value) => {
        if (value === null || value === undefined) return "";
        const raw = typeof value === "string" ? value.replace(/,/g, "") : String(value);
        const parsed = parseInt(raw, 10);
        return isNaN(parsed) ? "" : parsed.toLocaleString("en-US");
    };


    const handleSalaryChange = (setter) => (e) => {
        const raw = e.target.value.replace(/,/g, "");
        if (/^\d*$/.test(raw)) {
            setter(raw);
        }
    };

    const currencySymbol = useMemo(() => {
        return getCurrencySymbolFromCountry(country);
    }, [country]);
    const [salaryType, setSalaryType] = useState("annual");
    const [locationType, setLocationType] = useState("Remote");

    const jobIsUnlocked = job && job.lastPaidDate && (() => {
        const paidDate = new Date(job.lastPaidDate);
        const expirationDate = new Date(paidDate);
        expirationDate.setDate(expirationDate.getDate() + 30);
        expirationDate.setUTCHours(6, 0, 0, 0);
        return expirationDate > new Date();
    })();


    useEffect(() => {
        if (job) {
            setJobTitle(job.title || "");
            setJobUrl(job.url || "");
            setStatus(job.status || "Open");
            setSalaryMin(job.salaryMin || "");
            setSalaryMax(job.salaryMax || "");
            setSalaryType(job.salaryType || "annual");
            setLocationType(job.locationType || "Remote");
            setPostalCode(job.postalCode || "");
            setOffersRelocationAssistance(job.offersRelocationAssistance ?? true);
            setOffersSponsorship(job.offersSponsorship !== undefined ? job.offersSponsorship : true);

            // ✅ Ensure all targets are loaded
            if (Array.isArray(job.targets)) {
                setTargets([...job.targets]);
            } else {
                setTargets([]);
            }
        } else {
            setJobTitle("");
            setJobUrl("");
            setTargets([]);
            setStatus("Open");
            setSalaryMin( "");
            setSalaryMax( "");
            setSalaryType( "annual");
            setLocationType( "Remote");
            setPostalCode("");
            setOffersRelocationAssistance(true);
            setOffersSponsorship(true);

        }
    }, [job]);

    const handleAddTarget = () => {
        setTargets([...targets, { firstName: "", lastName: "", email: "", status: "invited", isNew: true, sendEmail: true }]);
    };

    const handleRemoveTarget = (index) => {
        setTargets(targets.filter((_, i) => i !== index));
    };

    const handleTargetChange = (index, field, value) => {
        const updatedTargets = [...targets];
        if (field === null && typeof value === "object") {
            updatedTargets[index] = {
                ...value,
                status: "invited",
                isNew: true,
                sendEmail: true
            };
        } else {
            updatedTargets[index][field] = value;
        }
        setTargets(updatedTargets);
    };


    const handleSave = async () => {
        const token = localStorage.getItem("token");
        if (!token) {
            alert("You must be logged in to save jobs.");
            return;
        }

        if ((locationType === "Hybrid" || locationType === "Onsite")) {
            const trimmedCountry = country.trim().toUpperCase();

            if (!countries.isValid(trimmedCountry)) {
                alert("Please enter a valid two-letter country code (e.g. US, MX, CA).");
                return;
            }

            if (!postcodeValidator(postalCode, trimmedCountry)) {
                alert("Please enter a valid postal code for the given country.");
                return;
            }
        }

        setLoading(true);
        try {
            let savedJob = job;
            if (!job?._id) {
                const response = await api.post(
                    "/jobs",
                    {
                        title: jobTitle,
                        url: jobUrl,
                        targets,
                        salaryMin: parseInt(String(salaryMin).replace(/,/g, ""), 10),
                        salaryMax: parseInt(String(salaryMax).replace(/,/g, ""), 10),
                        salaryType,
                        locationType,
                        postalCode,
                        country: country.trim().toUpperCase(),
                        offersRelocationAssistance,
                        offersSponsorship
                    },
                    {
                        headers: { Authorization: `Bearer ${token}` }
                    }
                );
                savedJob = response.data.job;
            } else {
                const response = await api.put(
                    `/jobs/${job._id}`,
                    {
                        title: jobTitle,
                        url: jobUrl,
                        status,
                        targets,
                        salaryMin: parseInt(String(salaryMin).replace(/,/g, ""), 10),
                        salaryMax: parseInt(String(salaryMax).replace(/,/g, ""), 10),
                        salaryType,
                        locationType,
                        postalCode,
                        country: country.trim().toUpperCase(),
                        offersRelocationAssistance,
                        offersSponsorship
                    },
                    {
                        headers: { Authorization: `Bearer ${token}` }
                    }
                );
                savedJob = response.data.job;
            }

            onSave(savedJob);
            onClose();
        } catch (error) {
            console.error("❌ Error saving job post:", error.response?.data || error);
        } finally {
            setLoading(false);
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50">
            <div className="bg-bluegray p-6 rounded-lg shadow-lg w-full max-w-5xl max-h-[90vh] overflow-y-auto relative">
                <button onClick={onClose} className="absolute top-2 right-2 text-lightgray text-2xl font-bold">
                    &times;
                </button>

                <h2 className="text-orange text-xl font-bold mb-4 text-center">
                    {job ? "Edit Job Post" : "Create Job Post"}
                </h2>

                <div className="flex flex-wrap gap-4">
                    <div className="w-full md:w-[48%]">
                        <label className="block text-lightgray font-semibold mb-1">Job Title</label>
                        <input
                            type="text"
                            value={jobTitle}
                            onChange={(e) => setJobTitle(e.target.value)}
                            className="w-full p-2 rounded bg-deepblue text-lightgray border border-lightblue focus:ring-2 focus:ring-orange"
                        />
                    </div>

                    <div className="w-full md:w-[48%]">
                        <label className="block text-lightgray font-semibold mb-1">Job URL</label>
                        <input
                            type="text"
                            value={jobUrl}
                            onChange={(e) => setJobUrl(e.target.value)}
                            className="w-full p-2 rounded bg-deepblue text-lightgray border border-lightblue focus:ring-2 focus:ring-orange"
                        />
                    </div>

                    <div className="w-full md:w-[48%]">
                        <label className="block text-lightgray font-semibold mb-1">Location Type</label>
                        <select
                            value={locationType}
                            onChange={(e) => setLocationType(e.target.value)}
                            className="w-full p-2 rounded bg-deepblue text-lightgray border border-lightblue"
                        >
                            <option value="Remote">Remote</option>
                            <option value="Hybrid">Hybrid</option>
                            <option value="Onsite">Onsite</option>
                        </select>
                    </div>

                    {["Onsite", "Hybrid"].includes(locationType) && (
                        <>
                            <div className="w-full md:w-[48%]">
                                <label className="block text-lightgray font-semibold mb-1">Country (e.g. US, MX)</label>
                                <input
                                    type="text"
                                    value={country}
                                    onChange={(e) => setCountry(e.target.value.replace(/[^A-Za-z]/g, "").toUpperCase())}
                                    placeholder="US"
                                    maxLength={2}
                                    className="w-full p-2 rounded bg-deepblue text-lightgray border border-lightblue"
                                />
                            </div>

                            <div className="w-full md:w-[48%]">
                                <label className="block text-lightgray font-semibold mb-1">ZIP/Postal Code</label>
                                <input
                                    type="text"
                                    value={postalCode}
                                    onChange={(e) => setPostalCode(e.target.value)}
                                    className="w-full p-2 rounded bg-deepblue text-lightgray border border-lightblue"
                                />
                            </div>

                        </>
                    )}

                    {/* Candidate Requirements Section */}
                    <div className="w-full mt-2">
                        <label className="block text-lightgray font-bold mb-2">Candidate Requirements</label>

                        <div className="flex items-center mb-2">
                            <input
                                type="checkbox"
                                id="offersSponsorship"
                                checked={offersSponsorship}
                                onChange={(e) => setOffersSponsorship(e.target.checked)}
                                className="mr-2 h-4 w-4"
                            />
                            <label htmlFor="offersSponsorship" className="text-lightgray">
                                Offers visa sponsorship <FieldHelpPopover fieldName="Sponsorship" content="Visa sponsorship availability. Candidates filter on this — setting it accurately improves match quality." />
                            </label>
                        </div>

                        {locationType !== "Remote" && (
                            <div className="flex items-center">
                                <input
                                    type="checkbox"
                                    id="offersRelocationAssistance"
                                    checked={offersRelocationAssistance}
                                    onChange={(e) => setOffersRelocationAssistance(e.target.checked)}
                                    className="mr-2 h-4 w-4"
                                />
                                <label htmlFor="offersRelocationAssistance" className="text-lightgray">
                                    Offers relocation assistance <FieldHelpPopover fieldName="Relocation" content="Check this if your company provides relocation assistance for this role. This helps match candidates who need relocation support." />
                                </label>
                            </div>
                        )}
                    </div>

                    <div className="w-full md:w-[48%]">
                        <label className="block text-lightgray font-semibold mb-1">Salary Min ({currencySymbol}) <FieldHelpPopover fieldName="Salary" content="Salary range helps filter candidates by fit and signals transparency to applicants. Enter annual figures. Leave blank if undisclosed." /></label>
                        <input
                            type="text"
                            placeholder="Min"
                            value={formatNumber(salaryMin)}
                            onChange={handleSalaryChange(setSalaryMin)}
                            className="w-full p-2 rounded bg-deepblue text-lightgray border border-lightblue"
                        />
                    </div>

                    <div className="w-full md:w-[48%]">
                        <label className="block text-lightgray font-semibold mb-1">Salary Max ({currencySymbol})</label>
                        <input
                            type="text"
                            placeholder="Max"
                            value={formatNumber(salaryMax)}
                            onChange={handleSalaryChange(setSalaryMax)}
                            className="w-full p-2 rounded bg-deepblue text-lightgray border border-lightblue"
                        />
                    </div>

                    <div className="w-full md:w-[48%]">
                        <label className="block text-lightgray font-semibold mb-1">Salary Type</label>
                        <select
                            value={salaryType}
                            onChange={(e) => setSalaryType(e.target.value)}
                            className="w-full p-2 rounded bg-deepblue text-lightgray border border-lightblue"
                        >
                            <option value="annual">Annual</option>
                            <option value="hourly">Hourly</option>
                        </select>
                    </div>

                    {job && (
                        <div className="w-full md:w-[48%]">
                            <label className="block text-lightgray font-semibold mb-1">Job Status</label>
                            <select
                                value={status}
                                onChange={(e) => setStatus(e.target.value)}
                                className="w-full p-2 rounded bg-deepblue text-white border border-lightblue focus:ring-2 focus:ring-orange"
                            >
                                <option value="Open">Open</option>
                                <option value="Filled">Filled</option>
                                <option value="Canceled">Canceled</option>
                            </select>
                        </div>
                    )}
                </div>

                {/* ✅ Targets Section */}
                <div className="mt-6 mb-4">
                    {!jobIsUnlocked && (
                        <button
                            onClick={handleAddTarget}
                            className="w-full bg-lightblue text-darkblue p-2 rounded hover:bg-orange mt-2"
                        >
                            + Add Target
                        </button>
                    )}
                    <label className="block text-lightgray font-semibold mb-2">Targets <FieldHelpPopover fieldName="Targets" content={<>Superstar Targets define who your ideal candidate looks like. The system matches every applicant against your targets' profiles. Add at least one person who excels in this role.{" "}<Link to="/how-to-pick-great-targets" className="text-lightblue underline hover:text-orange">Learn more</Link></>} /></label>
                    {targets.length === 0 ? (
                        <p className="text-gray-400 text-center py-2">No targets assigned.</p>
                    ) : (
                        <div className="space-y-2">
                            {targets.map((target, index) => (
                                <div key={index} className="relative bg-gray-700 p-3 rounded-md flex flex-col space-y-2">
                                    {!jobIsUnlocked && (
                                        <button
                                            onClick={() => handleRemoveTarget(index)}
                                            className="absolute -top-3 -right-3 bg-red-600 text-white text-sm w-6 h-6 flex items-center justify-center rounded-full hover:bg-red-700"
                                        >
                                            &times;
                                        </button>
                                    )}
                                    {(!jobIsUnlocked && target.isNew) ? (
                                        <>
                                            <input
                                                type="text"
                                                placeholder="First Name"
                                                value={target.firstName}
                                                onChange={(e) => handleTargetChange(index, "firstName", e.target.value)}
                                                className="w-full p-2 rounded bg-gray-800 text-lightgray border border-gray-600"
                                            />
                                            <input
                                                type="text"
                                                placeholder="Last Name"
                                                value={target.lastName}
                                                onChange={(e) => handleTargetChange(index, "lastName", e.target.value)}
                                                className="w-full p-2 rounded bg-gray-800 text-lightgray border border-gray-600"
                                            />
                                            <div className="relative">
                                                <input
                                                    type="email"
                                                    placeholder="Email"
                                                    value={target.email}
                                                    onChange={(e) => handleTargetChange(index, "email", e.target.value)}
                                                    className="w-full p-2 rounded bg-gray-800 text-lightgray border border-gray-600"
                                                />

                                                {target.email && (
                                                    <div className="absolute z-10 w-full bg-gray-800 border border-gray-600 mt-1 rounded max-h-40 overflow-y-auto">
                                                        {allTargetSuggestions
                                                            .filter(t =>
                                                                t.email.toLowerCase().includes(target.email.toLowerCase()) &&
                                                                !targets.some(existing => existing.email.toLowerCase() === t.email.toLowerCase())
                                                            )
                                                            .slice(0, 5)
                                                            .map((suggestion, idx) => (
                                                                <div
                                                                    key={idx}
                                                                    onClick={() => handleTargetChange(index, null, suggestion)}
                                                                    className="cursor-pointer p-2 hover:bg-gray-600 text-lightgray"
                                                                >
                                                                    {suggestion.firstName} {suggestion.lastName} - {suggestion.email}
                                                                </div>
                                                            ))}
                                                    </div>
                                                )}
                                            </div>

                                            <div className="flex items-center">
                                                <input
                                                    type="checkbox"
                                                    id={`sendEmailToTarget-${index}`}
                                                    checked={target.sendEmail}
                                                    onChange={() => handleTargetChange(index, "sendEmail", !target.sendEmail)}
                                                    className="mr-2 h-4 w-4"
                                                />
                                                <label htmlFor={`sendEmailToTarget-${index}`} className="text-lightgray">
                                                    Please notify my target now!
                                                </label>
                                            </div>
                                        </>
                                    ) : (
                                        <div className="text-lightgray">
                                            {target.firstName} {target.lastName} - {target.email}
                                        </div>
                                    )}
                                    <p className="text-sm font-bold text-white bg-green-500 px-2 py-1 rounded-md w-fit">
                                        {target.status.replace(/\b\w/g, (char) => char.toUpperCase())}
                                    </p>
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                <button
                    onClick={handleSave}
                    className="w-full p-3 bg-orange text-darkblue font-bold rounded hover:bg-deeporange text-lg"
                    disabled={loading}
                >
                    {loading ? "Updating..." : job ? "Update Job" : "Save Job"}
                </button>
            </div>
        </div>
    );

};

export default JobPostModal;
